<template>
  <div class="ctc-opps">
    <v-card flat class="rmr-table-shell">
      <rmr-table-header controls-class="ctc-opps__controls">
        <template #heading>
          <div class="ctc-opps__heading">
            <div class="rmr-table-shell__title">Opportunités Affilizz</div>
            <rmr-soft-badge
              label="Synchronisées toutes les 2 h"
              color="#4338CA"
              background="#EEF2FF"
              size="xs"
            />
          </div>
        </template>
        <template #controls>
          <div class="rmr-table-shell__filter-item rmr-table-shell__search">
            <label class="rmr-table-shell__filter-label">Recherche</label>
            <rmr-text-field
              v-model="search"
              :append-icon="icons.mdiMagnify"
              label="Rechercher un produit"
              single-line
              clearable
            />
          </div>
          <div class="rmr-table-shell__filter-item ctc-opps__merchant">
            <label class="rmr-table-shell__filter-label">Marchand</label>
            <rmr-select
              v-model="merchant"
              :items="merchantItems"
              aria-label="Filtrer par marchand"
            />
          </div>
        </template>
      </rmr-table-header>

      <v-card-text class="rmr-table-shell__body">
        <v-data-table
          :headers="headers"
          :items="rows"
          :loading="loading"
          :options="options"
          @update:options="setOptions"
          :server-items-length="total"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          loading-text="Chargement des opportunités Affilizz…"
          class="rmr-data-table"
        >
          <template v-slot:item.title="{ item }">
            <div class="ctc-opps__product">
              <img
                v-if="item.imageUrl"
                :src="item.imageUrl"
                :alt="item.title"
                class="ctc-opps__image"
              />
              <ctc-product-visual
                v-else
                :icon="item.visual.icon"
                :color="item.visual.color"
                size="sm"
              />
              <span class="ctc-opps__title">{{ item.title }}</span>
            </div>
          </template>

          <template v-slot:item.priceCurrent="{ item }">
            <strong>{{ formatPrice(item.priceCurrent, item.currency) }}</strong>
          </template>

          <template v-slot:item.priceOriginal="{ item }">
            <span class="ctc-opps__original">
              {{ formatPrice(item.priceOriginal, item.currency) }}
            </span>
          </template>

          <template v-slot:item.discount="{ item }">
            <span v-if="item.discount !== null" class="ctc-opps__discount">
              -{{ item.discount }} %
            </span>
            <span v-else class="ctc-opps__muted">—</span>
          </template>

          <template v-slot:item.detectedAt="{ item }">
            {{ formatDateTime(item.detectedAt) }}
          </template>

          <template v-slot:item.status="{ item }">
            <rmr-status-chip
              :label="statuses[item.status].label"
              :variant="statuses[item.status].variant"
              size="sm"
            />
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="rmr-data-table__actions ctc-opps__actions">
              <rmr-icon-button
                :icon="icons.mdiEyeOutline"
                variant="edit"
                tooltip="Voir la fiche"
                aria-label="Voir la fiche"
                @click="detailId = item.id"
              />
              <v-tooltip
                v-if="item.action"
                bottom
                :disabled="!item.action.reason"
              >
                <template #activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <rmr-action-button
                      :label="item.action.label"
                      :icon="icons.mdiPlusCircleOutline"
                      variant="ghost"
                      size="xs"
                      :disabled="item.action.disabled"
                      @click="$emit('create-article', item.id)"
                    />
                  </span>
                </template>
                {{ item.action.reason }}
              </v-tooltip>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="rmr-data-table__state">
              <div class="rmr-data-table__state-title">
                {{ hasFilters ? "Aucun résultat" : "Aucune opportunité" }}
              </div>
              <div class="rmr-data-table__state-text">
                {{
                  hasFilters
                    ? "Aucune opportunité ne correspond aux filtres."
                    : "Aucune opportunité Affilizz disponible."
                }}
              </div>
              <rmr-action-button
                v-if="hasFilters"
                label="Réinitialiser les filtres"
                :icon="icons.mdiRestore"
                variant="ghost"
                size="sm"
                class="mt-3"
                @click="resetFilters"
              />
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <ctc-opportunity-modal
      :value="!!detail"
      :opportunity="detail ? detail.opportunity : null"
      :action="detail ? detail.action : null"
      @input="onDetailToggle"
      @create-article="onCreateFromModal"
    />
  </div>
</template>

<script>
import {
  computed,
  onBeforeUnmount,
  reactive,
  ref,
  watch,
} from "@vue/composition-api";
import {
  mdiEyeOutline,
  mdiMagnify,
  mdiPlusCircleOutline,
  mdiRestore,
} from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrSoftBadge from "@/components/ui/feedback/RmrSoftBadge.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrTableHeader from "@/components/ui/layout/RmrTableHeader.vue";

import { MERCHANTS, OPPORTUNITY_STATUSES } from "../constants";
import {
  discountPercent,
  formatDateTime,
  formatPrice,
  merchantLabel,
} from "../ctc-model";
import CtcProductVisual from "../shared/CtcProductVisual.vue";
import CtcOpportunityModal from "./CtcOpportunityModal.vue";

const ALL_MERCHANTS = "all";

const HEADERS = [
  { text: "Produit", value: "title", sortable: false },
  { text: "Marchand", value: "merchantLabel", sortable: false },
  { text: "Prix actuel", value: "priceCurrent", align: "end", sortable: false },
  {
    text: "Ancien prix",
    value: "priceOriginal",
    align: "end",
    sortable: false,
  },
  { text: "Remise", value: "discount", align: "end", sortable: false },
  { text: "Détectée le", value: "detectedAt", sortable: false },
  { text: "Statut", value: "status", sortable: false },
  { text: "Actions", value: "actions", sortable: false, align: "end" },
];

export default {
  name: "CtcOpportunitiesView",
  components: {
    CtcOpportunityModal,
    CtcProductVisual,
    RmrActionButton,
    RmrIconButton,
    RmrSelect,
    RmrSoftBadge,
    RmrStatusChip,
    RmrTableHeader,
    RmrTextField,
  },
  props: {
    opportunities: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    limit: {
      type: Number,
      default: 10,
    },
    // Opportunity currently loaded in the creation flow
    activeOpportunityId: {
      type: String,
      default: null,
    },
    // Set when another opportunity's generation is still running
    startBlockedReason: {
      type: String,
      default: null,
    },
  },
  setup(props, { emit }) {
    const search = ref("");
    const merchant = ref(ALL_MERCHANTS);
    const detailId = ref(null);
    const options = reactive({
      page: props.page,
      itemsPerPage: props.limit,
      sortBy: [],
      sortDesc: [],
    });
    // Not :options.sync: it would replace `options` on the instance while the
    // watchers below keep observing this object, so no page would ever load.
    const setOptions = (value) => Object.assign(options, value);
    let filterTimer = null;

    const load = () =>
      emit("load", {
        page: options.page,
        limit: options.itemsPerPage,
        search: (search.value || "").trim() || undefined,
        shop: merchant.value === ALL_MERCHANTS ? undefined : merchant.value,
      });

    watch(
      () => [options.page, options.itemsPerPage],
      ([page, limit], [previousPage, previousLimit]) => {
        if (page === previousPage && limit === previousLimit) return;
        load();
      }
    );
    watch([search, merchant], () => {
      clearTimeout(filterTimer);
      filterTimer = setTimeout(() => {
        if (options.page !== 1) options.page = 1;
        else load();
      }, 300);
    });
    onBeforeUnmount(() => clearTimeout(filterTimer));

    /** What "Créer un article" does for this opportunity, if anything. */
    const actionFor = (opportunity) => {
      if (opportunity.status !== "new") return null;
      return {
        label: "Créer un article",
        disabled: !!props.startBlockedReason,
        reason: props.startBlockedReason || "",
      };
    };

    const hasFilters = computed(
      () => !!(search.value || "").trim() || merchant.value !== ALL_MERCHANTS
    );

    const rows = computed(() =>
      props.opportunities.map((opportunity) => ({
        id: opportunity.id,
        title: opportunity.product.title,
        visual: opportunity.product.visual,
        imageUrl: opportunity.product.imageUrl || "",
        merchantLabel: merchantLabel(opportunity.product.merchant),
        priceCurrent: opportunity.product.priceCurrent,
        priceOriginal: opportunity.product.priceOriginal,
        currency: opportunity.product.currency,
        discount: discountPercent(opportunity.product),
        detectedAt: opportunity.detectedAt,
        status: opportunity.status,
        action: actionFor(opportunity),
      }))
    );

    const detail = computed(() => {
      const opportunity = props.opportunities.find(
        ({ id }) => id === detailId.value
      );
      return opportunity
        ? { opportunity, action: actionFor(opportunity) }
        : null;
    });

    return {
      setOptions,
      detail,
      detailId,
      formatDateTime,
      formatPrice,
      hasFilters,
      headers: HEADERS,
      icons: {
        mdiEyeOutline,
        mdiMagnify,
        mdiPlusCircleOutline,
        mdiRestore,
      },
      merchant,
      merchantItems: [
        { text: "Tous les marchands", value: ALL_MERCHANTS },
        ...Object.keys(MERCHANTS).map((value) => ({
          text: MERCHANTS[value].label,
          value,
        })),
      ],
      options,
      onCreateFromModal: (id) => {
        detailId.value = null;
        emit("create-article", id);
      },
      onDetailToggle: (open) => {
        if (!open) detailId.value = null;
      },
      resetFilters: () => {
        search.value = "";
        merchant.value = ALL_MERCHANTS;
      },
      rows,
      search,
      statuses: OPPORTUNITY_STATUSES,
      total: computed(() => props.total),
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-opps__heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.ctc-opps ::v-deep .ctc-opps__controls {
  flex-wrap: wrap;
}

.ctc-opps__merchant {
  min-width: 200px;
}

.ctc-opps__product {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 220px;
  padding: 6px 0;
}

.ctc-opps__image {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  padding: 3px;
  border: 1px solid $rmr-border;
  border-radius: $rmr-radius-md;
  background: $rmr-surface-0;
  object-fit: contain;
}

.ctc-opps__title {
  font-weight: 700;
  color: $rmr-text-primary;
}

.ctc-opps__muted,
.ctc-opps__original {
  color: $rmr-text-muted;
}

.ctc-opps__original {
  text-decoration: line-through;
}

.ctc-opps__discount {
  font-weight: 800;
  color: $rmr-danger;
}

.ctc-opps__actions {
  flex-wrap: nowrap;
  gap: 6px;
}

@media (max-width: 599px) {
  .ctc-opps__product {
    min-width: 0;
  }

  .ctc-opps__merchant {
    min-width: 0;
    width: 100%;
  }

  .ctc-opps__actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}
</style>
